// src/AreaChart.js

import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import config from "../../../helper/config";
import { customerConstants } from "../../../helper/constants.js";
import { Dropdown } from "react-bootstrap";

const getCustomerFeatureFlag = () => {
  return config.customerConfigs.featureFlag;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

let BGColor, hoverBGColor;
const customer = getCustomerFeatureFlag();
switch (customer) {
  case customerConstants.EXO_CUSTOMER1:
    BGColor = {
      backgroundColor: [
        "#238BCE",
        "#1A8FE3",
        "#D9DBF1",
        "#7E8299",
        "#ffc47c",
        "#61678c",
        "#8ca4f4",
        "#afbef4",
        "#c4c4bd",
        "#8cdcb4",
        "#6c85ec",
        "#dbdcd6",
        "#94a4f4",
        "#bcbcc4",
        "#fce45f",
      ],
    };
    hoverBGColor = {
      backgroundColor: [
        "#5458AF",
        "#1A8FE3",
        "#D9DBF1",
        "#7E8299",
        "#ffc47c",
        "#61678c",
        "#8ca4f4",
        "#afbef4",
        "#c4c4bd",
        "#8cdcb4",
        "#6c85ec",
        "#dbdcd6",
        "#94a4f4",
        "#bcbcc4",
        "#fce45f",
      ],
    };
    break;
  case customerConstants.EXO_CUSTOMER2:
    BGColor = {
      backgroundColor: ["#216694", "#2980b9", "#5499c7", "#7fb3d5", "#a9cce3"],
    };
    hoverBGColor = {
      backgroundColor: ["#216694", "#2980b9", "#5499c7", "#7fb3d5", "#a9cce3"],
    };
    break;
  default:
    BGColor = {
      backgroundColor: [
        "#238BCE",
        "#32B965",
        "#7558A8",
        "#F249A4",
        "#D53364",
        "#FFA31E",
        "#fce45f",
        "#00AEE0",
      ],
    };
    hoverBGColor = {
      backgroundColor: [
        "#238BCE",
        "#32B965",
        "#7558A8",
        "#F249A4",
        "#D53364",
        "#FFA31E",
        "#fce45f",
        "#00AEE0",
      ],
    };
    break;
}

const AreaChart = (props) => {
  const [monthBasedReceivables, setMonthBasedReceivables] = useState(undefined);
  const [selectedReceivables, setSelectedReceivables] = useState([]);
  useEffect(() => {
    fetchSuitableReceivable();
  }, [props.val]);

  const fetchSuitableReceivable = () => {
    const groupedData = props.val?.reduce((acc, obj) => {
      const key = `${obj.flg_chart_legend_x_filter}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    if (groupedData) {
      const filteredGroupedData = Object.values(groupedData);

      setMonthBasedReceivables(filteredGroupedData);
      setSelectedReceivables(filteredGroupedData[0]);
    }
  };

  const groupedData = selectedReceivables?.reduce((acc, obj) => {
    const key = `${obj.chart_x_value}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
  const arrays = Object.values(groupedData);
  const colorArray = [
    [75, 192, 192],
    [153, 102, 255],
    [102, 253, 255],
    [95, 192, 212],
    [133, 82, 235],
  ];
  const datasets = arrays?.map((data, i) => {
    const dataArray = new Array(arrays.length).fill(0);

    dataArray[i] = data[0].chart_y_value;

    return {
      label: data[0].chart_x_value,
      data: dataArray,
      backgroundColor: `rgba(${colorArray[i][0]}, ${colorArray[i][1]}, ${colorArray[i][2]}, 0.2)`,
      borderColor: `rgba(${colorArray[i][0]}, ${colorArray[i][1]}, ${colorArray[i][2]}, 1)`,
      borderWidth: 0.5,
      fill: false,
      tension: 0,
    };
  });
  const labels = selectedReceivables?.map((val) => {
    const parts = val.chart_legend_y_title.split("|");
    if (parts.length > 1) {
      const subParts = parts[1].split("revenue:");
      if (subParts.length > 0) {
        return subParts[0].trim();
      }
    }
    return val.chart_legend_y_title;
  });

  const uniqueLabels = [...new Set(labels)];
  const data = {
    labels: uniqueLabels,
    datasets: datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          font: {
            family: "Poppins",
            size: 10,
          },
        },
      },
      title: {
        display: true,
        text: props.val[0]?.chart_title,
      },
      tooltip: {
        bodyFont: {
          family: "Poppins",
        },
        titleFont: {
          family: "Poppins",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "Poppins",
            size: 10,
          },
        },
        title: {
          display: true,
          // text: "Month",
          font: {
            family: "Poppins",
            size: 10,
          },
        },
      },
      y: {
        grid: {
          display: true,
        },
        ticks: {
          font: {
            family: "Poppins",
            size: 10,
          },
        },
        title: {
          display: true,
          // text: "Revenue",
          font: {
            family: "Poppins",
            size: 10,
          },
        },
      },
    },
    maintainAspectRatio: false,
    aspectRatio: 1,
  };

  return (
    <div className="w-100">
      <div className="row d-flex justify-content-between align-items-center my-3">
        <h3 className="dashboard__container-title m-0">
          {/* <SavingsRoundedIcon sx={{ fontSize: 22, color: "#5c5c5c" }} />{" "} */}
          Monthly Revenue
        </h3>
        <div className="">
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#fff",
                border: "1px solid #E4E6EF",
                color: "var(--secondary-text-color)",
                fontWeight: "400",
                fontSize: "10px",
              }}
            >
              {selectedReceivables &&
                selectedReceivables[0]?.flg_chart_legend_x_filter}
            </Dropdown.Toggle>
            <Dropdown.Menu className="" style={{ width: 'max-content' }}>
              {monthBasedReceivables &&
                monthBasedReceivables.map((obj) => (
                  <Dropdown.Item
                    onClick={() => {
                      setSelectedReceivables(obj);
                    }}
                  >
                    {obj[0]?.flg_chart_legend_x_filter}
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div style={{ height: "300px" }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default AreaChart;
